import { useState, type FormEvent } from "react";
import { Link } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Plus, Trash2 } from "lucide-react";
import { listDevAccounts, removeDevAccount, saveDevAccount, type DevAccountRow } from "@/lib/api";
import { formatTimestamp } from "@/lib/utils";
import { WorkspaceShell, usePreviewReadOnly } from "@/components/workspace/WorkspaceShell";
import "./admin.css";

const message = (error: unknown) => error instanceof Error ? error.message : String(error);

const shortDate = new Intl.DateTimeFormat("en-GB", { day: "numeric", month: "short", year: "numeric" });

export function DevAccountsWorkspace() {
  const readOnly = usePreviewReadOnly();
  const qc = useQueryClient();
  const [playerId, setPlayerId] = useState("");
  const [note, setNote] = useState("");
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editNote, setEditNote] = useState("");
  const query = useQuery({ queryKey: ["dev-accounts"], queryFn: listDevAccounts });
  const refresh = () => {
    void qc.invalidateQueries({ queryKey: ["dev-accounts"] });
    void qc.invalidateQueries({ queryKey: ["flags"] });
  };
  const save = useMutation({
    mutationFn: ({ id, value }: { id: string; value: string }) => saveDevAccount(id, value),
    onSuccess: (_result, variables) => {
      if (variables.id === editingId) { setEditingId(null); setEditNote(""); }
      else { setPlayerId(""); setNote(""); }
      refresh();
    },
  });
  const remove = useMutation({ mutationFn: removeDevAccount, onSuccess: refresh });

  function handleAdd(event: FormEvent) {
    event.preventDefault();
    const id = playerId.trim();
    if (!id || save.isPending || readOnly) return;
    save.mutate({ id, value: note.trim() });
  }
  function handleRemove(row: DevAccountRow) {
    if (!confirm(`Remove ${row.display_name ?? row.player_id} from developer accounts?`)) return;
    remove.mutate(row.player_id);
  }

  const rows = query.data ?? [];
  return <WorkspaceShell crumbs={[{ label: "Administration" }, { label: "Developer accounts" }]} readOnly={readOnly}>
    <div className="ws-scroll"><div className="ws-page">
      <div className="ws-head"><h1>Developer accounts</h1></div>
      <p className="ws-alert is-warn">Skips anti-cheat flags only. Bans and purchase evidence still apply.</p>
      <form onSubmit={handleAdd} className="ws-card ws-form">
        <div className="ad-add">
          <input className="ad-id" aria-label="Player ID" value={playerId} onChange={e => setPlayerId(e.target.value)} placeholder="Player ID" required />
          <input aria-label="Note" value={note} onChange={e => setNote(e.target.value)} placeholder="Note (optional)" />
          <button type="submit" className="pw-button pw-button-primary" title="Adding a listed player updates their note" disabled={!playerId.trim() || save.isPending || remove.isPending || readOnly}>
            <Plus size={14} />{save.isPending && editingId === null ? "Saving…" : "Add"}</button>
        </div>
        {save.error && editingId === null && <p className="ws-alert" role="alert">{message(save.error)}</p>}
      </form>
      <section className="ws-card is-flush ad-card-flush">
        <div className="ws-card-head"><h2>Whitelisted players</h2><span className="ws-muted">{rows.length} account{rows.length === 1 ? "" : "s"}</span></div>
        {query.error && <p className="ws-alert ad-card-error" role="alert">{message(query.error)}</p>}
        {remove.error && <p className="ws-alert ad-card-error" role="alert">{message(remove.error)}</p>}
        {query.isPending ? <div className="ws-empty" role="status"><span className="pw-loading-dot" /> Loading developer accounts…</div>
          : rows.length === 0 ? <p className="ws-empty">No developer accounts yet.</p>
          : <div className="ws-table ad-table" role="table" aria-label="Developer accounts">
            <div className="ws-table-row ws-table-head ad-dev-row" role="row">{["Player", "Note", "Added", "Added by", ""].map((h, i) => <span role="columnheader" key={i}>{h}</span>)}</div>
            {rows.map(row => <div key={row.player_id} className="ws-table-row ad-dev-row" role="row">
              <span role="cell" className="ad-main"><Link className="admin-account-link" to={`/accounts?player=${encodeURIComponent(row.player_id)}`}>{row.display_name ?? row.player_id}</Link>
                {row.display_name && <span className="ad-sub">{row.player_id}</span>}</span>
              {editingId === row.player_id ? <span role="cell" className="ad-note-edit ad-wide ws-form">
                <input aria-label={`Note for ${row.player_id}`} value={editNote} onChange={e => setEditNote(e.target.value)} autoFocus />
                <button className="pw-button pw-button-primary" onClick={() => save.mutate({ id: row.player_id, value: editNote.trim() })} disabled={save.isPending || readOnly}>{save.isPending ? "Saving…" : "Save"}</button>
                <button className="pw-button pw-button-plain" onClick={() => { setEditingId(null); save.reset(); }}>Cancel</button>
                {save.error && <span className="ad-note-error">{message(save.error)}</span>}
              </span> : <span role="cell" className="ad-wide"><button type="button" className="ad-note-button" title="Edit note" onClick={() => { save.reset(); setEditingId(row.player_id); setEditNote(row.note ?? ""); }}>
                {row.note || <em>Add note</em>}</button></span>}
              <span role="cell" className="ad-muted" title={formatTimestamp(row.added_at)}>{shortDate.format(row.added_at)}</span>
              <span role="cell" className="ad-mono ad-muted" data-label="by">{row.added_by ?? "—"}</span>
              <span role="cell" className="ad-end"><button className="pw-icon-button ad-remove" title="Remove" aria-label={`Remove ${row.player_id}`} disabled={remove.isPending || save.isPending || readOnly} onClick={() => handleRemove(row)}><Trash2 size={14} /></button></span>
              <span className="ad-break" aria-hidden="true" />
            </div>)}
          </div>}
      </section>
    </div></div>
  </WorkspaceShell>;
}
